import { type JSX } from 'react';
import { cn } from '@/lib/utils';

/**
 * KIR-4213: a labelled, masked evidence block — the full stored value rendered
 * in a scrollable `<pre>`, matching the KIR-4212 "Chat excerpt" panel.
 *
 * The backend builds the excerpt as a small WINDOW around the flagged command
 * (`command-evidence-excerpt.ts`, <= 800 chars, anchored on the flagged text,
 * `…` where trimmed) and secret-/PII-screens it, so `content` is safe to render
 * verbatim and there is nothing more to expand to. It is shown IN FULL — never
 * head-only-collapsed, which would hide the command tail (the exfil target — the
 * exact evidence KIR-4213 exists to surface). `max-w-3xl` + `break-all` keep a
 * long line readable instead of stretching to the card's full width.
 *
 * KIR-4836: `label` is optional — the tool-call card's own title already names
 * the block, so repeating it above the command would be noise. `className`
 * tunes the `<pre>` (the tool-call variant spans the full card width and takes
 * an accent border) without forking the styling.
 */
export function AlertEvidenceExcerpt({
  label,
  content,
  className,
}: {
  label?: string;
  content: string;
  className?: string;
}): JSX.Element {
  return (
    <div className="mt-2 flex flex-col gap-1">
      {label !== undefined && (
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      )}
      <pre
        className={cn(
          'max-h-72 max-w-3xl overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-all whitespace-pre-wrap dark:bg-slate-700',
          className
        )}
      >
        {content}
      </pre>
    </div>
  );
}
